namespace $.$$ {
	type ComplexKey = keyof ReturnType<$exso_sheets['columns']>
	type Key = Exclude<ComplexKey, `${string}__${string}`>

	export class $exso_sheets<Value extends Record<string, unknown> = Record<string, unknown>> extends $.$exso_sheets {
		@$mol_mem_key
		_compare([a, b]: [unknown, unknown]): number {
			if (a instanceof $mol_time_moment && b instanceof $mol_time_moment)
				return this._compare([a.valueOf(), b.valueOf()])
			if (typeof a === 'number' && typeof b === 'number') return a - b
			if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b)
			return 0
		}
		@$mol_mem
		version(next?: number) {
			return next ?? 0
		}
		@$mol_mem
		list(): Value[] {
			this.version()
			const list = this.paths().flatMap(path => {
				const body = this.$.$mol_fetch.json(this.api_path(path), { credentials: 'include' }) as Record<
					string,
					string
				>[]
				return body.map(row => this.parse(row))
			})
			const order_by = this.order_by().map(k => {
				if (k[0] === '-') return [k.substring(1), 'desc']
				return [k, 'asc']
			})
			list.sort((a, b) => {
				for (const [key, dir] of order_by) {
					const result = this._compare([a[key], b[key]])
					if (result !== 0) return dir === 'desc' ? -result : result
				}
				return 0
			})
			return list
		}

		@$mol_mem
		duration_formatter() {
			const intl = new Intl.DurationFormat(this.$.$mol_locale.lang(), {
				style: 'narrow',
			})
			return intl.format.bind(intl)
		}

		@$mol_mem_key
		minutes_format(minutes: number) {
			if (minutes === 0) return '0'
			return this.duration_formatter()({
				hours: Math.floor(minutes / 60),
				minutes: minutes % 60,
			})
		}

		@$mol_mem_key
		value([data, key]: [readonly Value[], key: string]) {
			const keys = key.split('__') as Key[]
			const unit =
				(this.columns()[key as ComplexKey] as any)?.unit ||
				keys.map(k => (this.columns()[k] as any)?.unit).join(' / ')
			if (unit) {
				try {
					const value = keys.reduce((acc, k, i) => {
						const value = this.value_raw([data, k as keyof Value])
						if (i === 0) return value
						if (value === 0) throw '-'
						return acc / value
					}, 0)
					if (unit === 'minutes') return this.minutes_format(value)
					return `${
						keys.length === 1 ? this.$.$exso_format.float2([value, 1]) : this.$.$exso_format.float(value)
					} ${unit}`
				} catch (e) {
					if (e === '-') return e
					throw e
				}
			}
			const values = Array.from(new Set(data.map(d => d[key as keyof Value])))
			if (values.length === 1) return `${values[0]}`
			return `${data.length}`
		}

		@$mol_mem_key
		value_raw([data, key]: [readonly Value[], keyof Value]) {
			return data.reduce((acc, v) => acc + (v[key] as number), 0)
		}

		unique<K extends keyof Value>([data, key]: [readonly Value[], K]) {
			return $exso_sheets.unique([data, key])
		}

		@$mol_mem_key
		hue([id, count]: [string, number]): string {
			const hash = this.$.$mol_hash_string(id)
			return `${((hash % count) * 360) / count}deg`
		}
		@($mol_mem_key as any)
		static unique<V extends Record<string, any>, K extends keyof V>([data, key]: [readonly V[], K]) {
			return Array.from(data.reduce((acc, v) => acc.add(v[key]), new Set<V[K]>()))
		}

		@$mol_mem_key
		static api_path(path: string) {
			if (this.$.$mol_dom.location.hostname === 'localhost') return `http://localhost:8090${path}`
			return path
		}
		api_path(path: string) {
			return $exso_sheets.api_path(path)
		}
		@$mol_action
		reload() {
			this.version(this.version() + 1)
		}
	}
}
